<template>
    <div
        class="vx-card flex flex-col"
        ref="card"
        :class="[
            { 'overflow-hidden': tempHidden },
            { 'no-shadow': noShadow },
            { 'rounded-none': noRadius },
            { 'card-border': cardBorder },
            cardClasses
        ]"
        :style="cardStyles"
        v-on="$listeners"
    >
        <div
            v-if="$slots['overlay']"
            class="absolute h-full w-full"
            style="z-index: 1000; pointer-events: none"
        >
            <slot name="overlay"></slot>
        </div>
        <div v-if="$slots['full-body']">
            <slot name="full-body"></slot>
        </div>
        <div
            v-if="hasHeader"
            ref="header"
            class="vx-card__header w-full items-start"
            :class="{ 'cursor-pointer': collapseAction }"
            @click="toggleContent"
        >
            <!-- card title -->
            <div class="vx-card__title">
                <slot name="title">
                    <h4 v-if="title" :style="titleStyles" :class="titleClasses" v-html="title" />
                    <h6
                        v-if="subtitle"
                        :style="subtitleStyles"
                        :class="subtitleClasses"
                        v-html="subtitle"
                    />

                    <slot name="subtitle" />
                </slot>
            </div>

            <!-- card actions -->
            <div class="vx-card__actions w-1/3" v-if="hasAction">
                <slot name="actions">
                    <div
                        class="vx-card__action-buttons"
                        v-if="
                            (actionButtons ||
                                collapseAction ||
                                refreshContentAction ||
                                removeCardAction) &&
                            !codeToggler
                        "
                    >
                        <feather-icon
                            icon="ChevronUpIcon"
                            :class="{ rotate180: !isContentCollapsed }"
                            class="ml-4"
                            v-if="actionButtons || collapseAction"
                        />
                        <feather-icon
                            @click="refreshcard"
                            icon="RotateCwIcon"
                            class="ml-4"
                            v-if="actionButtons || refreshContentAction"
                        />
                        <feather-icon
                            @click="removeCard"
                            icon="XIcon"
                            class="ml-4"
                            v-if="actionButtons || removeCardAction"
                        />
                    </div>
                    <div
                        class="vx-card__code-toggler sm:block hidden"
                        v-if="codeToggler && !actionButtons"
                    >
                        <feather-icon
                            icon="CodeIcon"
                            :class="{
                                'border border-solid border-primary border-t-0 border-r-0 border-l-0':
                                    showCode
                            }"
                            @click="toggleCode"
                        ></feather-icon>
                    </div>
                </slot>
            </div>
        </div>

        <div
            class="vx-card__collapsible-content vs-con-loading__container"
            ref="content"
            :class="[{ collapsed: isContentCollapsed }, { 'overflow-hidden': tempHidden }]"
            :style="styleItems"
        >
            <!-- content with no body(no padding) -->
            <div>
                <slot name="no-body"> </slot>
            </div>

            <!-- content inside body(with padding) -->
            <div
                v-if="$slots.default"
                class="vx-card__body h-full"
                :class="[
                    { 'no-header': !this.hasHeader },
                    { withHeaderFooter: this.hasHeader && $slots.footer }
                ]"
            >
                <slot></slot>
                <div v-if="$slots.footer && !isContentCollapsed" class="pt-4"></div>
            </div>

            <div v-if="showFooter" class="w-full absolute" style="z-index: 1001">
                <transition :name="footerTransition" appear>
                    <div
                        class="footer-wrapper w-full flex justify-end px-6"
                        style="transition-delay: 0.1s"
                    >
                        <slot name="footer"></slot>
                    </div>
                </transition>
            </div>
        </div>

        <div
            class="vx-card__code-container"
            ref="codeContainer"
            v-if="$slots.codeContainer"
            :style="codeContainerStyles"
            :class="{ collapsed: !showCode }"
        >
            <div class="code-content">
                <prism :language="codeLanguage" :key="$vs.rtl">
                    <slot name="codeContainer"></slot>
                </prism>
            </div>
        </div>
    </div>
</template>

<script>
import Prism from 'vue-prism-component'
import _color from '@/assets/utils/color.js'

export default {
    name: 'vx-card',
    props: {
        title: String,
        subtitle: String,
        actionButtons: {
            type: Boolean,
            default: false
        },
        actionButtonsColor: {
            type: String,
            default: 'success'
        },
        codeToggler: {
            type: Boolean,
            default: false
        },
        noShadow: {
            default: false,
            type: Boolean
        },
        noRadius: {
            default: false,
            type: Boolean
        },
        cardBorder: {
            default: false,
            type: Boolean
        },
        codeLanguage: {
            default: 'markup',
            type: String
        },
        collapseAction: {
            default: false,
            type: Boolean
        },
        refreshContentAction: {
            default: false,
            type: Boolean
        },
        removeCardAction: {
            default: false,
            type: Boolean
        },
        cardBackground: {
            default: '',
            type: String
        },
        contentColor: {
            default: '',
            type: String
        },
        titleColor: {
            default: '',
            type: String
        },
        subtitleColor: {
            default: '#b8c2cc',
            type: String
        },
        /**
         * Allows the spinner to be controlled through the parent component
         * EG: When awaiting an API call - set true initially, then false after the API call is completed
         */
        isLoading: {
            type: Boolean,
            required: false
        },
        isCollapsed: {
            type: Boolean,
            required: false
        },
        hideFooter: {
            type: Boolean,
            default: false
        },
        footerTransition: {
            type: String,
            default: 'slide-fade-left'
        }
    },
    watch: {
        isLoading: {
            handler: function (v) {
                if (v) {
                    this.loading = true
                } else {
                    this.loading = false
                }
            },
            immediate: true
        },
        isCollapsed: {
            handler: function (v) {
                if (this.isContentCollapsed != v) {
                    this.toggleContent()
                }
            }
        }
    },
    data() {
        return {
            isContentCollapsed: false,
            showCode: false,
            maxHeight: null,
            cardMaxHeight: null,
            codeContainerMaxHeight: '0px',
            tempHidden: false,
            collapseTimeout: null
        }
    },
    computed: {
        hasAction() {
            return (
                this.$slots.actions ||
                this.actionButtons ||
                this.collapseAction ||
                this.refreshContentAction ||
                this.removeCardAction ||
                this.codeToggler
            )
        },
        hasHeader() {
            return this.$slots.title || this.hasAction || this.title || this.subtitle
        },
        headerStyle() {
            return {
                'background-color': _color.getColor(this.headerBackground)
            }
        },
        styleItems() {
            return { maxHeight: this.maxHeight }
        },
        cardStyles() {
            let obj = { maxHeight: this.cardMaxHeight }
            if (this.cardBackground == 'mid-grey') {
            }
            obj.background = _color.getColor(this.cardBackground)
            obj.color = _color.getColor(this.contentColor)
            // if (!_color.isColor(this.cardBackground))
            // if (!_color.isColor(this.contentColor))
            return obj
        },
        codeContainerStyles() {
            return { maxHeight: this.codeContainerMaxHeight }
        },
        cardClasses() {
            let str = ''

            // Add bg class
            if (_color.isColor(this.cardBackground)) {
                str += ` bg-${this.cardBackground}`
            }

            // add content color
            if (_color.isColor(this.contentColor)) {
                str += ` text-${this.contentColor}`
            }

            return str
        },
        titleStyles() {
            return {
                color: _color.getColor(this.titleColor)
            }
        },
        titleClasses() {
            let str = ''

            // add content color
            if (_color.isColor(this.titleColor)) {
                str += ` text-${this.titleColor}`
            }

            return str.trim()
        },
        subtitleStyles() {
            let obj = {}
            if (!_color.isColor(this.subtitleColor)) obj.color = _color.getColor(this.subtitleColor)

            return obj
        },
        subtitleClasses() {
            let str = ''

            // add content color
            if (_color.isColor(this.subtitleColor)) {
                str += ` text-${this.subtitleColor}`
            }

            return str.trim()
        },
        showFooter() {
            return this.$slots.footer && !this.isContentCollapsed && !this.hideFooter
        }
    },
    methods: {
        toggleContent(e) {
            if (!this.collapseAction) {
                return
            }

            if (this.collapseTimeout != null) {
                clearTimeout(this.collapseTimeout)
                this.collapseTimeout = null
            }

            let scrollHeight = this.$refs.content.scrollHeight
            this.maxHeight = `${scrollHeight}px`
            if (this.isContentCollapsed) {
                this.$refs.content.style.overflow = 'hidden'
                this.collapseTimeout = setTimeout(() => {
                    this.maxHeight = 'none'
                    this.$refs.content.style.overflow = null
                    this.collapseTimeout = null
                }, 300)
            } else {
                this.collapseTimeout = setTimeout(() => {
                    this.maxHeight = `0rem`
                    // this.$refs.content.style.overflow = null
                    this.collapseTimeout = null
                }, 50)
            }
            this.isContentCollapsed = !this.isContentCollapsed
            if (this.isCollapsed != this.isContentCollapsed) {
                this.$emit('update:isCollapsed', this.isContentCollapsed)
            }
        },
        refreshcard() {
            this.$vs.loading({
                container: this.$refs.content,
                scale: 0.5
            })
            this.tempHidden = true
            this.$emit('refresh', this)
        },
        removeRefreshAnimation(time = 100) {
            setTimeout(() => {
                this.$vs.loading.close(this.$refs.content)
                this.tempHidden = false
            }, time)
        },
        removeCard() {
            let scrollHeight = this.$refs.card.scrollHeight
            this.cardMaxHeight = `${scrollHeight}px`
            this.$el.style.overflow = 'hidden'
            setTimeout(() => {
                this.cardMaxHeight = `0px`
            }, 50)
            this.$emit('remove')
        },
        toggleCode() {
            this.tempHidden = true
            this.showCode = !this.showCode
            let scrollHeight = this.$refs.codeContainer.scrollHeight
            if (this.codeContainerMaxHeight == '0px') {
                this.codeContainerMaxHeight = `${scrollHeight}px`
                setTimeout(() => {
                    this.codeContainerMaxHeight = 'none'
                    this.tempHidden = false
                }, 300)
            } else {
                this.codeContainerMaxHeight = `${scrollHeight}px`
                setTimeout(() => {
                    this.codeContainerMaxHeight = `0px`
                    this.tempHidden = false
                }, 150)
            }
        }
    },
    mounted() {
        if (this.isCollapsed) {
            this.maxHeight = `0rem`
            this.$refs.content.style.overflow = 'hidden'
            this.isContentCollapsed = true
        }
    },
    components: {
        Prism
    }
}
</script>

<style lang="scss">
@import '@/assets/scss/vuexy/components/vxCard.scss';
.footer-wrapper {
    margin-top: -1.35rem !important;
}
.withHeaderFooter {
    padding: 0 1.5rem 3rem !important;
}
</style>
